// OOXML style cascade (word/styles.xml).
//
// Word resolves the effective run/paragraph properties by layering, in order:
//   docDefaults  →  paragraph-style chain (basedOn, base-most first)  →
//   character-style chain (runs only)  →  direct pPr/rPr on the node.
// Later layers override earlier ones. This module builds that model once and
// exposes resolvers the extractor calls per paragraph/run.
import { attrByLocalName, childByLocalName, childrenByLocalName, intAttr, val } from './readDocxXml';
import type { ResolvedParagraphProps, ResolvedRunProps } from './types';

interface StyleDef {
    type: string;
    basedOn: string | null;
    pPr: Element | null;
    rPr: Element | null;
}

export interface StylesData {
    styles: Map<string, StyleDef>;
    docDefaultsPPr: Element | null;
    docDefaultsRPr: Element | null;
}

export function parseStyles(stylesDoc: Document | null): StylesData {
    const styles = new Map<string, StyleDef>();
    let docDefaultsPPr: Element | null = null;
    let docDefaultsRPr: Element | null = null;

    const root = stylesDoc?.documentElement ?? null;
    if (!root) return { styles, docDefaultsPPr, docDefaultsRPr };

    const docDefaults = childByLocalName(root, 'docDefaults');
    if (docDefaults) {
        docDefaultsPPr = childByLocalName(childByLocalName(docDefaults, 'pPrDefault'), 'pPr');
        docDefaultsRPr = childByLocalName(childByLocalName(docDefaults, 'rPrDefault'), 'rPr');
    }

    for (const styleEl of childrenByLocalName(root, 'style')) {
        const id = attrByLocalName(styleEl, 'styleId');
        if (!id) continue;
        styles.set(id, {
            type: attrByLocalName(styleEl, 'type') ?? '',
            basedOn: val(childByLocalName(styleEl, 'basedOn')),
            pPr: childByLocalName(styleEl, 'pPr'),
            rPr: childByLocalName(styleEl, 'rPr'),
        });
    }

    return { styles, docDefaultsPPr, docDefaultsRPr };
}

// Style chain from base-most ancestor to the style itself, so a caller can
// apply them left-to-right and let the derived style win. `visited` guards the
// (malformed but real) case of a basedOn cycle.
function styleChain(styleId: string | null, data: StylesData): StyleDef[] {
    const chain: StyleDef[] = [];
    const visited = new Set<string>();
    let current = styleId;
    while (current && !visited.has(current)) {
        visited.add(current);
        const def = data.styles.get(current);
        if (!def) break;
        chain.push(def);
        current = def.basedOn;
    }
    return chain.reverse();
}

function readRunPr(rPr: Element | null, into: ResolvedRunProps): void {
    if (!rPr) return;

    const sz = val(childByLocalName(rPr, 'sz'));
    if (sz != null) {
        const parsed = parseInt(sz, 10);
        if (Number.isFinite(parsed)) into.sz = parsed;
    }

    const color = val(childByLocalName(rPr, 'color'));
    if (color != null) into.color = color;

    const highlight = val(childByLocalName(rPr, 'highlight'));
    if (highlight != null) into.highlight = highlight;

    const shd = childByLocalName(rPr, 'shd');
    if (shd) {
        const fill = attrByLocalName(shd, 'fill');
        if (fill != null) into.shdFill = fill;
    }

    // <w:u> present with any val other than "none" is an underline; a bare
    // <w:u w:val="none"/> in a later layer explicitly clears an inherited one.
    const u = childByLocalName(rPr, 'u');
    if (u) into.underline = val(u) !== 'none';
}

function readParagraphPr(pPr: Element | null, into: ResolvedParagraphProps): void {
    if (!pPr) return;

    const jc = val(childByLocalName(pPr, 'jc'));
    if (jc != null) into.jc = jc;

    const spacing = childByLocalName(pPr, 'spacing');
    if (spacing) {
        into.spacing = into.spacing ?? {};
        const line = intAttr(spacing, 'line');
        if (line != null) into.spacing.line = line;
        const lineRule = attrByLocalName(spacing, 'lineRule');
        if (lineRule != null) into.spacing.lineRule = lineRule;
        const before = intAttr(spacing, 'before');
        if (before != null) into.spacing.before = before;
        const after = intAttr(spacing, 'after');
        if (after != null) into.spacing.after = after;
    }

    const numPr = childByLocalName(pPr, 'numPr');
    if (numPr) {
        const numId = val(childByLocalName(numPr, 'numId'));
        if (numId != null) {
            const ilvlRaw = val(childByLocalName(numPr, 'ilvl'));
            const ilvl = ilvlRaw != null ? parseInt(ilvlRaw, 10) : 0;
            into.numPr = { ilvl: Number.isFinite(ilvl) ? ilvl : 0, numId };
        }
    }
}

export function resolveRunProps(
    paragraphStyleId: string | null,
    runStyleId: string | null,
    directRPr: Element | null,
    data: StylesData,
): ResolvedRunProps {
    const out: ResolvedRunProps = {};
    readRunPr(data.docDefaultsRPr, out);
    for (const def of styleChain(paragraphStyleId, data)) readRunPr(def.rPr, out);
    for (const def of styleChain(runStyleId, data)) readRunPr(def.rPr, out);
    readRunPr(directRPr, out);
    return out;
}

export function resolveParagraphProps(
    paragraphStyleId: string | null,
    directPPr: Element | null,
    data: StylesData,
): ResolvedParagraphProps {
    const out: ResolvedParagraphProps = {};
    readParagraphPr(data.docDefaultsPPr, out);
    for (const def of styleChain(paragraphStyleId, data)) readParagraphPr(def.pPr, out);
    readParagraphPr(directPPr, out);
    return out;
}
